/* eslint-disable @angular-eslint/no-empty-lifecycle-method */
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, forwardRef } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DatePickerModule } from 'primeng/datepicker';
import { FloatLabel } from 'primeng/floatlabel';

@Component({
  selector: 'app-date-picker',
  standalone: true,
  imports: [DatePickerModule, CommonModule, FormsModule, FloatLabel],
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePickerComponent),
      multi: true,
    },
  ],
})
export class DatePickerComponent implements OnInit, ControlValueAccessor {
  selectedDate: Date | null = null;
  @Input() iconDisplay = 'input';
  @Input() showWeek = false;
  @Input() showButtonBar = true;
  @Input() inline = true;
  @Input() placeholder = 'dd-mm-aaaa';
  @Input() label = '';
  @Input() minDate: Date | null = null;
  @Input() keepInvalid = false;
  @Input() type: 'single' | 'multiple' | 'range' = 'single';
  @Input() size: 'sm' | 'md' | 'lg' = 'md';
  @Output() selectedDateRange = new EventEmitter<string | string[]>();
  get sizeClass(): string {
    switch (this.size) {
      case 'sm':
        return 'datepicker-sm';
      case 'lg':
        return 'datepicker-lg';
      case 'md':
      default:
        return 'datepicker-md';
    }
  }
  private propagateChange: (date: Date | Date[] | null) => void = () => {};

  localeEs = {
    firstDayOfWeek: 1,
    dayNames: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
    dayNamesShort: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
    dayNamesMin: ['D', 'L', 'M', 'X', 'J', 'V', 'S'],
    monthNames: [
      'enero',
      'febrero',
      'marzo',
      'abril',
      'mayo',
      'junio',
      'julio',
      'agosto',
      'septiembre',
      'octubre',
      'noviembre',
      'diciembre',
    ],
    monthNamesShort: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
    today: 'Hoy',
    clear: 'Limpiar',
    dateFormat: 'dd/mm/yy',
  };
  private propagateTouched: () => void = () => {};

  ngOnInit(): void {}

  writeValue(value: Date | null): void {
    this.selectedDate = value;
  }

  registerOnChange(registerFn: (date: Date | null) => void): void {
    this.propagateChange = registerFn;
  }

  registerOnTouched(registerFn: () => void): void {
    this.propagateTouched = registerFn;
  }

  clearSelection(): void {
    this.selectedDate = null;
    this.onDateChanged(null);
  }

  onDateChanged(newDate: Date | null | Date[]) {
    this.selectedDate = newDate as Date;
    this.propagateChange(newDate as Date);
    if (Array.isArray(newDate)) {
      // Si es un rango de fechas o múltiples
      const formattedDates = newDate.map((date) => this.formatDate(date));
      this.selectedDateRange.emit(formattedDates);
    } else {
      // Si es una sola fecha
      const formattedDate = newDate ? this.formatDate(newDate) : null;
      this.selectedDateRange.emit(formattedDate);
    }
  }
  private formatDate(date: Date | null): string {
    if (!date) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  onBlur() {
    this.propagateTouched();
  }
}
